import { useState, useEffect } from 'react';
import type { HTMLAttributes } from 'react';
import { createPortal } from 'react-dom';
import styled from '@emotion/styled';
import { theme, scaled } from '../styles/theme';
import { Spinner } from '../styles/components';
import { fetchSubmissionChallenge, submitFeedback } from '../utils/api';
import { CityAutocomplete, type CitySelection } from './CityAutocomplete';
import type { Announcement, UserSubmissionBody } from '../types';
import { stateLabel, useT, type Messages } from '../i18n';

// User feedback dialog — ONE form for both modes:
//   'correction'  — "Korrektur vorschlagen": the same fields, prefilled with
//                   the announcement's current values; the user edits what's
//                   wrong and must provide a source for the claim.
//   'new_project' — "Investitionsprojekt vorschlagen": empty form.
// Submissions go to POST /api/user-submissions and land in the admin's
// moderation queue (which diffs corrections against the live row); nothing
// changes in the live data without review.

interface FeedbackDialogProps {
  mode: 'correction' | 'new_project';
  /** Required in correction mode: the announcement being reported. */
  announcement?: Announcement;
  onClose: () => void;
}

// --- Chrome (mirrors ExportDialog) ---

const Overlay = styled.div<{ isVisible: boolean }>`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  /* Top of the stack, one above AnnouncementModal (2147483646) so the
     correction form opened from it stacks correctly. */
  z-index: 2147483647;
  opacity: ${props => props.isVisible ? 1 : 0};
  visibility: ${props => props.isVisible ? 'visible' : 'hidden'};
  transition: opacity 0.2s ease;

  @media (max-width: 600px) {
    padding: ${theme.spacing.md};
    align-items: flex-start;
  }
`;

const DialogContainer = styled.div<{ isVisible: boolean }>`
  background: ${theme.colors.surface};
  border-radius: ${theme.borderRadius.lg};
  box-shadow: 0 12px 32px rgba(24, 28, 68, 0.18), 0 4px 8px rgba(0, 0, 0, 0.06);
  width: 480px;
  max-width: 90vw;
  /* Taller than the export dialog — scroll inside rather than growing past
     the viewport. */
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  transform: ${props => props.isVisible ? 'scale(1)' : 'scale(0.95)'};
  transition: transform 0.2s cubic-bezier(0.4, 0, 0.2, 1);

  @media (max-width: 600px) {
    width: 100%;
    max-width: none;
    margin-top: ${theme.spacing.sm};
  }
`;

const DialogHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  border-bottom: 1px solid ${theme.colors.border};
  flex-shrink: 0;
`;

const DialogTitle = styled.h3`
  font-family: var(--sans-stack);
  font-size: ${scaled(16)};
  font-weight: ${theme.fontWeight.semibold};
  color: var(--color-text-default);
  margin: 0;
`;

const CloseButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  font-size: 16px;
  border: none;
  background: transparent;
  border-radius: ${theme.borderRadius.sm};
  cursor: pointer;
  color: ${theme.colors.text.secondary};
  transition: all 0.15s ease;

  &:hover {
    background: rgba(24, 28, 68, 0.08);
    color: ${theme.colors.text.primary};
  }
`;

const DialogContent = styled.div`
  padding: ${theme.spacing.lg};
  overflow-y: auto;

  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb { background: ${theme.colors.border}; border-radius: 3px; }
`;

const DialogFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${theme.spacing.sm};
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  border-top: 1px solid ${theme.colors.border};
  flex-shrink: 0;
`;

// Shared `.button.button--{primary|outline}.button--size-m` (see ExportDialog).
const ButtonBase = styled.div`
  &[aria-disabled='true'] { opacity: 0.6; cursor: not-allowed; pointer-events: none; }
`;
interface DialogButtonProps extends HTMLAttributes<HTMLDivElement> {
  variant?: 'primary' | 'secondary';
}
const Button = ({ variant = 'secondary', className, ...rest }: DialogButtonProps) => (
  <ButtonBase
    {...rest}
    className={`button button--${variant === 'primary' ? 'primary' : 'outline'} button--size-m ${className ?? ''}`.trim()}
  />
);

// --- Form primitives ---

const Intro = styled.p`
  font-family: var(--sans-stack);
  font-size: ${theme.fontSize.sm};
  color: ${theme.colors.text.secondary};
  margin: 0 0 ${theme.spacing.md} 0;
  line-height: 1.5;
`;

const ContextBox = styled.div`
  font-family: var(--sans-stack);
  font-size: ${theme.fontSize.sm};
  color: ${theme.colors.text.primary};
  background: rgba(24, 28, 68, 0.04);
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  margin-bottom: ${theme.spacing.md};
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: ${theme.spacing.md};
`;

const FieldLabel = styled.label`
  font-family: var(--sans-stack);
  font-size: ${theme.fontSize.xs};
  font-weight: ${theme.fontWeight.medium};
  color: ${theme.colors.text.primary};
`;

const FieldHint = styled.span`
  font-family: var(--sans-stack);
  font-size: ${theme.fontSize.xs};
  color: ${theme.colors.text.muted};
  line-height: 1.4;
`;

const inputStyles = `
  font-family: var(--sans-stack);
  font-size: ${scaled(14)};
  color: inherit;
  padding: 8px 10px;
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.sm};
  background: ${theme.colors.surface};
  outline: none;
  width: 100%;
  box-sizing: border-box;

  &:focus { border-color: ${theme.colors.primary}; }
  &::placeholder { color: ${theme.colors.text.muted}; }
  &[readonly] {
    background: rgba(24, 28, 68, 0.04);
    color: ${theme.colors.text.secondary};
  }
`;

const TextInput = styled.input`${inputStyles}`;
const Select = styled.select`${inputStyles}`;
const TextArea = styled.textarea`
  ${inputStyles}
  resize: vertical;
  min-height: 72px;
`;

// Honeypot: visually removed but present in the DOM. Real users never see or
// fill it; naive bots do and get silently dropped server-side.
const HoneypotWrap = styled.div`
  position: absolute;
  left: -9999px;
  top: -9999px;
  height: 1px;
  overflow: hidden;
`;

const ErrorText = styled.p`
  font-family: var(--sans-stack);
  font-size: ${theme.fontSize.sm};
  color: #B3261E;
  margin: 0 0 ${theme.spacing.md} 0;
`;

const CenterState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing.md};
  padding: ${theme.spacing.xl};
  text-align: center;
  font-family: var(--sans-stack);
  font-size: ${theme.fontSize.sm};
  color: ${theme.colors.text.primary};
`;

// --- Field metadata ---

const FEDERAL_STATES = [
  'Baden-Württemberg', 'Bayern', 'Berlin', 'Brandenburg', 'Bremen', 'Hamburg',
  'Hessen', 'Mecklenburg-Vorpommern', 'Niedersachsen', 'Nordrhein-Westfalen',
  'Rheinland-Pfalz', 'Saarland', 'Sachsen', 'Sachsen-Anhalt',
  'Schleswig-Holstein', 'Thüringen',
];

// The stored values stay English; the labels come from the dictionary.
const INVESTMENT_TYPES: { value: 'Greenfield expansion' | 'Brownfield expansion'; label: (m: Messages) => string }[] = [
  { value: 'Greenfield expansion', label: m => m.feedback.types.greenfield },
  { value: 'Brownfield expansion', label: m => m.feedback.types.brownfield },
];

const STATUSES: { value: 'announced' | 'in progress' | 'finished' | 'cancelled'; label: (m: Messages) => string }[] = [
  { value: 'announced', label: m => m.feedback.statuses.announced },
  { value: 'in progress', label: m => m.feedback.statuses.inProgress },
  { value: 'finished', label: m => m.feedback.statuses.finished },
  { value: 'cancelled', label: m => m.feedback.statuses.cancelled },
];

const URL_RE = /^https?:\/\/\S+$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// YYYY-MM-DD for <input type="date">, in LOCAL time. The API serializes dates
// as UTC timestamps ("2026-04-08T22:00:00.000Z" for April 9 in Berlin), so a
// plain slice(0,10) would prefill the previous day.
function toDateInput(v: string | null | undefined): string {
  if (!v) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
  const d = new Date(v);
  if (isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

type DialogState = 'form' | 'submitting' | 'success' | 'error';

export function FeedbackDialog({ mode, announcement, onClose }: FeedbackDialogProps) {
  const m = useT();
  const [isVisible, setIsVisible] = useState(false);
  const [state, setState] = useState<DialogState>('form');
  const [token, setToken] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);

  // Shared fields
  const [honeypot, setHoneypot] = useState('');
  const [message, setMessage] = useState('');
  const [sourceUrl, setSourceUrl] = useState('');
  const [email, setEmail] = useState('');

  // In correction mode the form is prefilled with the announcement's current
  // values — the user edits what's wrong. `announcement` is stable for the
  // dialog's lifetime, so lazy useState initializers are enough.
  const a = mode === 'correction' ? announcement : undefined;
  const loc0 = a?.locations?.[0];

  const [company, setCompany] = useState(a?.investing_company ?? '');
  const [projectName, setProjectName] = useState(a?.project_name ?? '');
  const [city, setCity] = useState(loc0?.city_name ?? '');
  // Set when the city has coordinates — either picked from the autocomplete
  // or prefilled from the announcement's existing location. Cleared on any
  // manual edit of the city text.
  const [cityPicked, setCityPicked] = useState<CitySelection | null>(
    loc0 && loc0.latitude != null && loc0.longitude != null
      ? { city_name: loc0.city_name, federal_state: loc0.federal_state, latitude: loc0.latitude, longitude: loc0.longitude }
      : null,
  );
  // The lookup service errored → free-text fallback is allowed.
  const [cityLookupFailed, setCityLookupFailed] = useState(false);
  // Suggestion count for the current text: a pick is only required when
  // there was actually something to pick (stays 0 until the user types).
  const [cityHitCount, setCityHitCount] = useState(0);
  const [federalState, setFederalState] = useState(
    loc0?.federal_state && FEDERAL_STATES.includes(loc0.federal_state) ? loc0.federal_state : '',
  );
  const [investmentType, setInvestmentType] = useState(
    a?.type_of_investment && INVESTMENT_TYPES.some(t => t.value === a.type_of_investment) ? a.type_of_investment : '',
  );
  const [status, setStatus] = useState(
    a?.investment_status && STATUSES.some(s => s.value === a.investment_status) ? a.investment_status : '',
  );
  const [volumeMio, setVolumeMio] = useState(
    a?.investment_volume_value_eur != null ? String(a.investment_volume_value_eur / 1_000_000) : '',
  );
  const [jobs, setJobs] = useState(a?.jobs_created != null ? String(a.jobs_created) : '');
  const [eventDate, setEventDate] = useState(toDateInput(a?.announced_event_date));

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 10);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && state !== 'submitting') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose, state]);

  // Fresh challenge token per dialog open; the server enforces a minimum age,
  // so by the time a human has filled the form it is valid.
  useEffect(() => {
    let cancelled = false;
    fetchSubmissionChallenge()
      .then(t => { if (!cancelled) setToken(t); })
      .catch(() => { if (!cancelled) setToken(null); });
    return () => { cancelled = true; };
  }, []);

  const handleOverlayClick = (event: React.MouseEvent) => {
    if (event.target === event.currentTarget && state !== 'submitting') onClose();
  };

  function validate(): string | null {
    if (!company.trim()) return m.feedback.errors.company;
    if (mode === 'new_project' && !city.trim()) return m.feedback.errors.city;
    // Require a pick from the suggestions so the project lands correctly on
    // der Karte — except when the lookup is down or produced no hits (then
    // the free text is resolved during review). Only triggers after typing:
    // a prefilled, untouched city keeps cityHitCount at 0.
    if (city.trim() && !cityPicked && !cityLookupFailed && cityHitCount > 0) {
      return m.feedback.errors.cityPick;
    }
    if (!sourceUrl.trim()) {
      return mode === 'correction'
        ? m.feedback.errors.sourceCorrection
        : m.feedback.errors.source;
    }
    if (!URL_RE.test(sourceUrl.trim())) {
      return m.feedback.errors.sourceUrl;
    }
    if (email.trim() && !EMAIL_RE.test(email.trim())) {
      return m.feedback.errors.email;
    }
    if (volumeMio.trim() && !(Number(volumeMio.replace(',', '.')) >= 0)) {
      return m.feedback.errors.volume;
    }
    if (jobs.trim() && !(Number.isInteger(Number(jobs)) && Number(jobs) >= 0)) {
      return m.feedback.errors.jobs;
    }
    return null;
  }

  async function handleSubmit() {
    const err = validate();
    if (err) {
      setValidationError(err);
      return;
    }
    if (!token) {
      setValidationError(m.feedback.errors.init);
      return;
    }
    setValidationError(null);
    setState('submitting');

    const projectFields = {
      investing_company: company.trim(),
      project_name: projectName.trim() || undefined,
      city: city.trim() || undefined,
      federal_state: federalState || undefined,
      latitude: cityPicked?.latitude,
      longitude: cityPicked?.longitude,
      type_of_investment: (investmentType || undefined) as 'Greenfield expansion' | 'Brownfield expansion' | undefined,
      investment_status: (status || undefined) as 'announced' | 'in progress' | 'finished' | 'cancelled' | undefined,
      investment_volume_value_eur: volumeMio.trim() ? Math.round(Number(volumeMio.replace(',', '.')) * 1_000_000) : undefined,
      jobs_created: jobs.trim() ? Number(jobs) : undefined,
      announced_event_date: eventDate || undefined,
      token,
      website: honeypot,
      message: message.trim() || undefined,
      source_url: sourceUrl.trim(),
      submitter_email: email.trim() || undefined,
    };

    const body: UserSubmissionBody =
      mode === 'correction'
        ? { kind: 'correction', announcement_id: announcement!.id, cluster_id: announcement!.cluster_id, ...projectFields }
        : { kind: 'new_project', ...projectFields, city: city.trim() };

    try {
      await submitFeedback(body);
      setState('success');
    } catch {
      setState('error');
    }
  }

  const title = mode === 'correction' ? m.feedback.titleCorrection : m.feedback.titleNewProject;

  const dialogContent = (
    <Overlay className="color-data" isVisible={isVisible} onClick={handleOverlayClick}>
      <DialogContainer isVisible={isVisible} onClick={e => e.stopPropagation()}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <CloseButton
            onClick={() => { if (state !== 'submitting') onClose(); }}
            aria-disabled={state === 'submitting'}
            title={m.common.close}
          >
            <span className="icon" data-icon="close" />
          </CloseButton>
        </DialogHeader>

        {state === 'submitting' && (
          <CenterState>
            <Spinner />
            <span>{m.feedback.sending}</span>
          </CenterState>
        )}

        {state === 'success' && (
          <>
            <CenterState>
              <span>
                <strong>{m.feedback.thanks}</strong><br />
                {m.feedback.reviewNote}
              </span>
            </CenterState>
            <DialogFooter>
              <Button variant="primary" onClick={onClose}>{m.common.close}</Button>
            </DialogFooter>
          </>
        )}

        {state === 'error' && (
          <>
            <CenterState>
              <span>
                {m.feedback.sendFailed}{' '}
                <a href="mailto:alexander.marx@dezernatzukunft.org">alexander.marx@dezernatzukunft.org</a>.
              </span>
            </CenterState>
            <DialogFooter>
              <Button variant="secondary" onClick={onClose}>{m.common.close}</Button>
              <Button variant="primary" onClick={() => setState('form')}>{m.feedback.backToForm}</Button>
            </DialogFooter>
          </>
        )}

        {state === 'form' && (
          <>
            <DialogContent>
              {mode === 'correction' ? (
                <>
                  <Intro>{m.feedback.introCorrection}</Intro>
                  <ContextBox>
                    <strong>{announcement?.investing_company ?? '-'}</strong>
                    {announcement?.project_name ? ` — ${announcement.project_name}` : ''}
                  </ContextBox>
                </>
              ) : (
                <Intro>{m.feedback.introNewProject}</Intro>
              )}

              <FieldGroup>
                <FieldLabel htmlFor="fb-company">{m.feedback.company}</FieldLabel>
                <TextInput id="fb-company" maxLength={200} value={company} onChange={e => setCompany(e.target.value)} placeholder={m.feedback.companyPlaceholder} />
              </FieldGroup>

                  <FieldGroup>
                    <FieldLabel htmlFor="fb-project">{m.feedback.project}</FieldLabel>
                    <TextInput id="fb-project" maxLength={200} value={projectName} onChange={e => setProjectName(e.target.value)} placeholder={m.feedback.projectPlaceholder} />
                  </FieldGroup>

                  <FieldGroup>
                    <FieldLabel htmlFor="fb-city">{mode === 'new_project' ? m.feedback.cityRequired : m.feedback.city}</FieldLabel>
                    <CityAutocomplete
                      id="fb-city"
                      value={city}
                      onChange={v => { setCity(v); setCityPicked(null); }}
                      onSelect={sel => {
                        setCity(sel.city_name);
                        setCityPicked(sel);
                        if (sel.federal_state) setFederalState(sel.federal_state);
                      }}
                      onLookupFailure={() => setCityLookupFailed(true)}
                      onResults={setCityHitCount}
                      placeholder={m.feedback.cityPlaceholder}
                    />
                    {cityPicked && (
                      <FieldHint>
                        {m.feedback.cityRecognised(
                          cityPicked.city_name
                          + (cityPicked.federal_state ? ` (${stateLabel(cityPicked.federal_state)})` : ''),
                        )}
                      </FieldHint>
                    )}
                    {!cityPicked && cityLookupFailed && (
                      <FieldHint>{m.feedback.cityLookupDown}</FieldHint>
                    )}
                    {mode === 'correction' && (announcement?.locations.length ?? 0) > 1 && (
                      <FieldHint>{m.feedback.multipleLocations}</FieldHint>
                    )}
                  </FieldGroup>

                  <FieldGroup>
                    <FieldLabel htmlFor="fb-state">{m.feedback.federalState}</FieldLabel>
                    <Select id="fb-state" value={federalState} onChange={e => setFederalState(e.target.value)}>
                      <option value="">{m.feedback.pleaseChoose}</option>
                      {FEDERAL_STATES.map(s => <option key={s} value={s}>{stateLabel(s)}</option>)}
                    </Select>
                  </FieldGroup>

                  <FieldGroup>
                    <FieldLabel htmlFor="fb-type">{m.feedback.type}</FieldLabel>
                    <Select id="fb-type" value={investmentType} onChange={e => setInvestmentType(e.target.value)}>
                      <option value="">{m.feedback.pleaseChoose}</option>
                      {INVESTMENT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label(m)}</option>)}
                    </Select>
                  </FieldGroup>

                  <FieldGroup>
                    <FieldLabel htmlFor="fb-status">{m.feedback.status}</FieldLabel>
                    <Select id="fb-status" value={status} onChange={e => setStatus(e.target.value)}>
                      <option value="">{m.feedback.pleaseChoose}</option>
                      {STATUSES.map(s => <option key={s.value} value={s.value}>{s.label(m)}</option>)}
                    </Select>
                  </FieldGroup>

                  <FieldGroup>
                    <FieldLabel htmlFor="fb-volume">{m.feedback.volume}</FieldLabel>
                    <TextInput id="fb-volume" inputMode="decimal" value={volumeMio} onChange={e => setVolumeMio(e.target.value)} placeholder={m.feedback.volumePlaceholder} />
                  </FieldGroup>

                  <FieldGroup>
                    <FieldLabel htmlFor="fb-jobs">{m.feedback.jobs}</FieldLabel>
                    <TextInput id="fb-jobs" inputMode="numeric" value={jobs} onChange={e => setJobs(e.target.value)} placeholder={m.feedback.jobsPlaceholder} />
                  </FieldGroup>

                  <FieldGroup>
                    <FieldLabel htmlFor="fb-date">{m.feedback.date}</FieldLabel>
                    <TextInput id="fb-date" type="date" value={eventDate} onChange={e => setEventDate(e.target.value)} />
                  </FieldGroup>

                  <FieldGroup>
                    <FieldLabel htmlFor="fb-source">{m.feedback.source}</FieldLabel>
                    <TextInput
                      id="fb-source"
                      type="url"
                      maxLength={500}
                      value={sourceUrl}
                      onChange={e => setSourceUrl(e.target.value)}
                      placeholder="https://…"
                    />
                    <FieldHint>
                      {mode === 'correction'
                        ? m.feedback.sourceHintCorrection
                        : m.feedback.sourceHint}
                    </FieldHint>
                  </FieldGroup>

                  <FieldGroup>
                    <FieldLabel htmlFor="fb-message">
                      {mode === 'correction' ? m.feedback.explanation : m.feedback.description}
                    </FieldLabel>
                    <TextArea
                      id="fb-message"
                      maxLength={2000}
                      value={message}
                      onChange={e => setMessage(e.target.value)}
                      placeholder={mode === 'correction' ? m.feedback.messagePlaceholderCorrection : m.feedback.messagePlaceholder}
                    />
                  </FieldGroup>

              <FieldGroup>
                <FieldLabel htmlFor="fb-email">{m.feedback.email}</FieldLabel>
                <TextInput
                  id="fb-email"
                  type="email"
                  maxLength={254}
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder={m.feedback.emailPlaceholder}
                />
                <FieldHint>{m.feedback.emailHint}</FieldHint>
              </FieldGroup>

              <HoneypotWrap aria-hidden="true">
                <label htmlFor="fb-website">Website</label>
                <input
                  id="fb-website"
                  name="website"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  value={honeypot}
                  onChange={e => setHoneypot(e.target.value)}
                />
              </HoneypotWrap>

              {validationError && <ErrorText>{validationError}</ErrorText>}
            </DialogContent>

            <DialogFooter>
              <Button variant="secondary" onClick={onClose}>{m.common.cancel}</Button>
              <Button variant="primary" onClick={handleSubmit}>{m.feedback.submit}</Button>
            </DialogFooter>
          </>
        )}
      </DialogContainer>
    </Overlay>
  );

  return createPortal(dialogContent, document.body);
}
